/**
 * Offline Storage Utility
 * Uses IndexedDB for persistent offline storage of orders
 */

const DB_NAME = 'hambre_offline_db';
const DB_VERSION = 1;
const STORES = {
  PENDING_ORDERS: 'pending_orders',
  MENU_CACHE: 'menu_cache',
  SYNC_QUEUE: 'sync_queue'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      console.log('IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create pending orders store
      if (!database.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const ordersStore = database.createObjectStore(STORES.PENDING_ORDERS, {
          keyPath: 'localId',
          autoIncrement: true
        });
        ordersStore.createIndex('status', 'status', { unique: false });
        ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Create menu cache store
      if (!database.objectStoreNames.contains(STORES.MENU_CACHE)) {
        const menuStore = database.createObjectStore(STORES.MENU_CACHE, {
          keyPath: 'restaurantId'
        });
        menuStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // Create sync queue store
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true
        });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('priority', 'priority', { unique: false });
      }
    };
  });
};

/**
 * Get the database connection
 */
const getDB = async () => {
  if (!db) {
    await initOfflineDB();
  }
  return db;
};

// ==================== PENDING ORDERS ====================

/**
 * Save a pending order for offline sync
 */
export const savePendingOrder = async (order) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PENDING_ORDERS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ORDERS);

    const orderData = {
      ...order,
      localId: order.localId || `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending',
      createdAt: new Date().toISOString(),
      syncAttempts: 0
    };

    const request = store.add(orderData);

    request.onsuccess = () => {
      console.log('Order saved for offline sync:', orderData.localId);
      resolve(orderData);
    };

    request.onerror = (event) => {
      console.error('Error saving pending order:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Get all pending orders
 */
export const getPendingOrders = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PENDING_ORDERS], 'readonly');
    const store = transaction.objectStore(STORES.PENDING_ORDERS);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = (event) => {
      console.error('Error getting pending orders:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Get count of pending orders
 */
export const getPendingOrderCount = async () => {
  const orders = await getPendingOrders();
  return orders.length;
};

/**
 * Mark an order as synced
 */
export const markOrderSynced = async (localId, serverId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PENDING_ORDERS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ORDERS);
    
    // Find the order by localId
    const cursorRequest = store.openCursor();
    
    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.localId === localId) {
          const updatedOrder = {
            ...cursor.value,
            status: 'synced',
            serverId,
            syncedAt: new Date().toISOString()
          };
          const updateRequest = cursor.update(updatedOrder);
          updateRequest.onsuccess = () => resolve(updatedOrder);
          updateRequest.onerror = (e) => reject(e.target.error);
        } else {
          cursor.continue();
        }
      } else {
        reject(new Error('Order not found'));
      }
    };

    cursorRequest.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Delete a synced order
 */
export const deleteSyncedOrder = async (localId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PENDING_ORDERS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ORDERS);
    
    const cursorRequest = store.openCursor();
    
    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.localId === localId) {
          const deleteRequest = cursor.delete();
          deleteRequest.onsuccess = () => resolve(true);
          deleteRequest.onerror = (e) => reject(e.target.error);
        } else {
          cursor.continue();
        }
      } else {
        resolve(false);
      }
    };

    cursorRequest.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Clear all synced orders
 */
export const clearSyncedOrders = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.PENDING_ORDERS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ORDERS);
    
    const cursorRequest = store.openCursor();
    let deletedCount = 0;
    
    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.status === 'synced') {
          cursor.delete();
          deletedCount++;
        }
        cursor.continue();
      } else {
        resolve(deletedCount);
      }
    };

    cursorRequest.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

// ==================== MENU CACHE ====================

/**
 * Cache menu items for a restaurant
 */
export const cacheMenuItems = async (restaurantId, menuItems) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MENU_CACHE], 'readwrite');
    const store = transaction.objectStore(STORES.MENU_CACHE);

    const cacheData = {
      restaurantId,
      items: menuItems,
      updatedAt: new Date().toISOString()
    };

    const request = store.put(cacheData);

    request.onsuccess = () => {
      console.log('Menu cached for restaurant:', restaurantId);
      resolve(cacheData);
    };

    request.onerror = (event) => {
      console.error('Error caching menu:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Get cached menu for a restaurant
 */
export const getCachedMenu = async (restaurantId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.MENU_CACHE], 'readonly');
    const store = transaction.objectStore(STORES.MENU_CACHE);
    const request = store.get(restaurantId);

    request.onsuccess = () => {
      resolve(request.result?.items || null);
    };

    request.onerror = (event) => {
      console.error('Error getting cached menu:', event.target.error);
      reject(event.target.error);
    };
  });
};

// ==================== SYNC QUEUE ====================

/**
 * Add item to sync queue
 */
export const addToSyncQueue = async (type, data, priority = 1) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);

    const queueItem = {
      type,
      data,
      priority,
      createdAt: new Date().toISOString(),
      attempts: 0
    };

    const request = store.add(queueItem);

    request.onsuccess = () => {
      resolve(queueItem);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Get all items from sync queue
 */
export const getSyncQueue = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.getAll();

    request.onsuccess = () => {
      // Sort by priority (higher first) then by createdAt (older first)
      const items = request.result || [];
      items.sort((a, b) => {
        if (a.priority !== b.priority) {
          return b.priority - a.priority;
        }
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
      resolve(items);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Remove item from sync queue
 */
export const removeFromSyncQueue = async (id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Clear the entire sync queue
 */
export const clearSyncQueue = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.clear();

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

// Initialize on import
initOfflineDB().catch(console.error);

export default {
  initOfflineDB,
  savePendingOrder,
  getPendingOrders,
  getPendingOrderCount,
  markOrderSynced,
  deleteSyncedOrder,
  clearSyncedOrders,
  cacheMenuItems,
  getCachedMenu,
  addToSyncQueue,
  getSyncQueue,
  removeFromSyncQueue,
  clearSyncQueue
};
